/**
 * Where a menu goes, given where it was asked for and how much room there is.
 *
 * Pure and file-local for the reason `libraryDrag.ts` is: `npm test` has no DOM, so a rule is only
 * reachable if it is not inside a component. That is also the argument for doing this arithmetic
 * by hand rather than handing it to floating-ui — flip and clamp are the two things that must be
 * exactly right for a pointer-anchored menu, and inside a library they would be verifiable only in
 * a browser, which is the gap 日本-322 exists about.
 *
 * ## Why `fixed` rather than `absolute`
 *
 * A right-click opens at arbitrary pointer coordinates, which `absolute`-inside-`relative` cannot
 * express at all. Every menu therefore renders from the provider, in the viewport's coordinate
 * space, and these functions answer in that space.
 *
 * Two things follow, and both are gains rather than costs. The three menus this replaces were each
 * `absolute` inside a scroll container and so **clippable** — the failure `TagChips.tsx` had to
 * solve once already, and which is invisible to a role query because a clipped menu is still
 * present. And an unfocused sentence row carries `opacity: .58` (`Read.tsx`), which creates a
 * stacking context, so its menu could paint *under* a later row. Neither is reachable from here.
 *
 * `fixed` is viewport-positioned only while no ancestor carries `transform`, `filter`, `contain`
 * or `perspective` — none does today, anywhere in `components/shell/` or the surfaces. If one ever
 * appears, the fix is `createPortal` at the render site; these functions do not change.
 */

/** Distance kept from every viewport edge, so a flipped menu never sits flush against one. */
const MARGIN = 6;

/** How far a submenu overlaps its parent, so the diagonal travel into it stays on the menu. */
const SUBMENU_OVERLAP = 4;

export interface Rect {
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * The corner the menu would like to occupy.
 *
 * `x`/`y` is the preferred top-left — the pointer for a right-click, or a trigger's bottom-left.
 * `flipX`/`flipY` are the coordinates to use *instead* when the menu will not fit, and they are
 * separate because the two openers flip differently: a pointer menu flips about the pointer
 * itself, while a menu hung off a `⋯` flips about the **opposite edge of the trigger**, so it
 * never lands on top of the button that opened it.
 */
export interface MenuAnchor {
  x: number;
  y: number;
  flipX?: number;
  flipY?: number;
}

/**
 * Place a menu, flipping before clamping.
 *
 * The order matters: clamping first would slide a menu that *could* have opened upwards into a
 * position overlapping the pointer, which reads as the menu having opened in the wrong place. A
 * menu too large to fit either way is clamped, and clamping wins — being able to see the top of a
 * menu beats honouring the anchor.
 */
export function placeMenu(
  anchor: MenuAnchor,
  size: Rect,
  viewport: Viewport,
  margin: number = MARGIN,
): { left: number; top: number } {
  let left = anchor.x;
  if (left + size.width + margin > viewport.width) {
    left = (anchor.flipX ?? anchor.x) - size.width;
  }

  let top = anchor.y;
  if (top + size.height + margin > viewport.height) {
    top = (anchor.flipY ?? anchor.y) - size.height;
  }

  return {
    left: clamp(left, margin, viewport.width - size.width - margin),
    top: clamp(top, margin, viewport.height - size.height - margin),
  };
}

/**
 * Place a submenu beside the item that opened it.
 *
 * It opens to the right and flips to the left, which is the convention every desktop menu uses;
 * `side` is returned because the caller needs it for the chevron and for which edge the overlap
 * comes off.
 *
 * The vertical rule is *not* a flip. A submenu is aligned with its parent item rather than hung
 * from a corner, so a long one near the bottom of the screen should slide up just enough to fit —
 * flipping it above the item would leave it pointing at nothing.
 */
export function placeSubmenu(
  parentItem: { left: number; right: number; top: number },
  size: Rect,
  viewport: Viewport,
  margin: number = MARGIN,
): { left: number; top: number; side: "right" | "left" } {
  const rightEdge = parentItem.right - SUBMENU_OVERLAP;
  const fitsRight = rightEdge + size.width + margin <= viewport.width;
  const side: "right" | "left" = fitsRight ? "right" : "left";
  const left = fitsRight ? rightEdge : parentItem.left - size.width + SUBMENU_OVERLAP;

  return {
    left: clamp(left, margin, Math.max(margin, viewport.width - size.width - margin)),
    top: clamp(parentItem.top, margin, Math.max(margin, viewport.height - size.height - margin)),
    side,
  };
}

function clamp(value: number, low: number, high: number): number {
  // `low` last, so a menu taller or wider than the viewport is pinned to the top-left and its
  // start stays readable, rather than being pushed off the top by a negative upper bound.
  return Math.max(low, Math.min(value, high));
}
